import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import { SEX_OPTIONS } from "../lib/constants";

const SEGMENTS = [
  { key: "missing", title: "Desaparecidos", color: "#1E3A8A", match: (r) => r.case_type === "missing" },
  { key: "found_alive", title: "Encontrados", color: "#047857", match: (r) => r.case_type === "found_alive" },
  { key: "all", title: "Todos", color: "#D97706", match: () => true },
];

export function AdminBreakdownCharts() {
  const [rows, setRows] = useState(null);
  const [sex, setSex] = useState("");
  const [minor, setMinor] = useState("");
  const [zone, setZone] = useState("");

  useEffect(() => {
    api.get("/admin/chart-breakdown").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, []);

  const zones = useMemo(
    () => (rows ? [...new Set(rows.map((r) => r.region))].sort() : []),
    [rows]
  );

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter(
      (r) =>
        (!sex || r.sex === sex) &&
        (minor === "" || r.is_minor === (minor === "true")) &&
        (!zone || r.region === zone)
    );
  }, [rows, sex, minor, zone]);

  const dataFor = (seg) => {
    const byRegion = {};
    filtered.filter(seg.match).forEach((r) => {
      byRegion[r.region] = (byRegion[r.region] || 0) + r.count;
    });
    return Object.entries(byRegion)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  if (!rows) return null;

  const resetFilters = () => { setSex(""); setMinor(""); setZone(""); };
  const filtersActive = sex || minor || zone;

  return (
    <section data-testid="admin-breakdown">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-heading text-lg font-semibold">Estadísticas por zona, género y menores</h2>
        <div className="flex flex-wrap gap-2">
          <select data-testid="breakdown-sex" value={sex} onChange={(e) => setSex(e.target.value)}
            className="h-10 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm">
            <option value="">Género (todos)</option>
            {SEX_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select data-testid="breakdown-minor" value={minor} onChange={(e) => setMinor(e.target.value)}
            className="h-10 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm">
            <option value="">Menores (todos)</option>
            <option value="true">Solo menores</option>
            <option value="false">Solo adultos</option>
          </select>
          <select data-testid="breakdown-zone" value={zone} onChange={(e) => setZone(e.target.value)}
            className="h-10 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm">
            <option value="">Zona (todas)</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          {filtersActive && (
            <button onClick={resetFilters} data-testid="breakdown-clear"
              className="h-10 px-3 rounded-lg bg-[#F1F5F9] text-sm font-medium text-[#475569]">Limpiar</button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {SEGMENTS.map((seg) => {
          const d = dataFor(seg);
          const total = d.reduce((s, x) => s + x.value, 0);
          return (
            <div key={seg.key} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`breakdown-chart-${seg.key}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm">{seg.title}</h3>
                <span className="font-heading text-lg font-bold" style={{ color: seg.color }} data-testid={`breakdown-total-${seg.key}`}>{total}</span>
              </div>
              {d.length ? (
                <ResponsiveContainer width="100%" height={Math.max(160, d.length * 30)}>
                  <BarChart data={d} layout="vertical" margin={{ left: 0, right: 24 }}>
                    <XAxis type="number" hide allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={95} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill={seg.color} radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 11, fill: "#475569" }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#94A3B8] py-8 text-center">Sin datos con estos filtros.</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
